import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { CardStack, Input, CopyInput, Select } from '@pearsontechnology/aero-components';
import enhanceWithCohort from 'hocs/enhanceWithCohort';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import Sidebar from 'Workspace/components/WorkspaceSidebar';
import cohortShape from 'Workspace/types/cohorts/cohort';
import {
  PageWrapper,
  PageViewport,
  PageContent,
} from 'components/PageStyles';
import RadioButton, { RadioButtonGroup } from 'components/RadioButton';
import gtmService from 'utils/gtm/gtmService';
import { GTM_EVENT_TYPES } from 'utils/gtm/constants';
import { featureKeys } from 'services/FeatureFlagService';
import { createRandomString } from 'utils/createRandomString';
import workspaceService from 'services/WorkspaceService';
import CohortPageHeader from '../CohortPageHeader';
import CohortNavigation from '../CohortNavigation';
import { enrollmentTypes } from '../constants';
import {
  SELECTABLE_PRODUCT_MODELS,
  PRODUCT_MODEL_LABEL_MESSAGES,
  PRODUCT_MODEL_TYPES,
  getProductModelValue,
  isValidProductModel,
} from '../CohortsPage/productModelConstants';

import SimpleColorInput from './SimpleColorInput';
import messages from './messages';
import {
  InputWrapper, LTIRadioWrapper, LTIShowHideButton, LTIKeyContainer,
  LTIKeySectionContainer, LTIKeySectionLabel, ProductModelFieldWrapper,
} from './styles';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';
import { connect } from 'react-redux';
import { commandRevokePermissionOnCohort,
} from 'containers/ConnectionManager/actions';

export function CohortSettings({
  cohort = {},
  updateCohort,
  getFeatureFlagValue,
  revokePermission,
}) {
  const { summary, settings } = cohort;
  const isLTIEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_CLASS_LTI_ENROLLMENT);
  const [showKeys, setShowKeys] = useState(false);

  useEffect(() => {
    if (location) {
      const { pathname } = location;
      gtmService.gtmTrackEvent({ eventType: GTM_EVENT_TYPES.PAGEVIEW, payload: { pageName: pathname } });
    }
  }, []);

  const handleEnrollmentChange = (event) => {
    if (event.target.value === enrollmentTypes.LTI && !settings.ltiConsumerCredentials) {
      updateCohortWithCredentials(event);
    } else {
      updateCohort({ enrollmentType: event.target.value });
    }
  };

  const updateCohortWithCredentials = (event) => {
    const workspaceId = workspaceService.getSelectedWorkspaceId();
    const credentials = {
      key: createRandomString(24),
      secret: createRandomString(36),
    };
    updateCohort({ enrollmentType: event.target.value, ltiConsumerCredential: credentials, workspaceId });
  };

  if (!summary || !settings) {
    return null;
  }

  const updateProductId = (event) => {
    const productId = event.target.value.trim();

    if (productId === settings.productId) return;

    updateCohort({
      productId: productId || null,
    });
  };

  const updateProductModel = (event) => {
    const productModel = event.target.value;
    const currentProductModel = getProductModelValue(summary, settings);

    if (productModel === currentProductModel) return;

    updateCohort(
      isValidProductModel(productModel)
        ? { productModel }
        : { productModel: null },
    );
  };

  const toggleKeysVisibility = () => setShowKeys(!showKeys);
  const productModelValue = getProductModelValue(summary, settings);

  return (
    <PageWrapper>
      <Sidebar navigation={<CohortNavigation cohortId={summary.cohortId} selectedKey="settings" />} />
      <PageViewport>
        <CohortPageHeader
          cohort={cohort}
          title={intl.formatMessage(messages.title)}
          updateCohort={updateCohort}
          onRevoke={(account)=>{revokePermission(account, cohort.summary.cohortId);}}
        />
        <PageContent>
          <CardStack
            title={intl.formatMessage(messages.productTitle)}
            description={intl.formatMessage(messages.productDescription)}
          >
            <Input
              type="text"
              label={intl.formatMessage(messages.productIdLabel)}
              placeholder={intl.formatMessage(messages.productIdPlaceholder)}
              defaultValue={settings.productId}
              onBlur={updateProductId}
            />
            <ProductModelFieldWrapper>
              <Select
                label={intl.formatMessage(messages.productModel)}
                value={productModelValue}
                onChange={updateProductModel}
              >
                <option value={PRODUCT_MODEL_TYPES.NONE}>
                  {intl.formatMessage(PRODUCT_MODEL_LABEL_MESSAGES[PRODUCT_MODEL_TYPES.NONE])}
                </option>
                {SELECTABLE_PRODUCT_MODELS.map((model) => (
                  <option key={model} value={model}>
                    {intl.formatMessage(PRODUCT_MODEL_LABEL_MESSAGES[model])}
                  </option>
                ))}
              </Select>
            </ProductModelFieldWrapper>
          </CardStack>

          <CardStack
            title={intl.formatMessage(messages.enrollmentTitle)}
            description={intl.formatMessage(messages.enrollmentDescription)}
          >
            <RadioButtonGroup>
              <RadioButton
                label={intl.formatMessage(messages.open)}
                name="enrollment-type"
                value={enrollmentTypes.OPEN}
                checked={summary.enrollmentType === enrollmentTypes.OPEN}
                onChange={handleEnrollmentChange}
              />
              <RadioButton
                label={intl.formatMessage(messages.passport)}
                name="enrollment-type"
                value={enrollmentTypes.PASSPORT}
                checked={summary.enrollmentType === enrollmentTypes.PASSPORT}
                onChange={handleEnrollmentChange}
              />
              {isLTIEnabled && (
                <>
                  <LTIRadioWrapper>
                    <RadioButton
                      label={intl.formatMessage(messages.lti)}
                      name="enrollment-type"
                      value={enrollmentTypes.LTI}
                      checked={summary.enrollmentType === enrollmentTypes.LTI}
                      onChange={handleEnrollmentChange}
                    />
                    {summary.enrollmentType === enrollmentTypes.LTI && (
                      <LTIShowHideButton chip flat onClick={toggleKeysVisibility}>
                        {showKeys
                          ? intl.formatMessage(messages.hideKeys)
                          : intl.formatMessage(messages.showKeys)
                        }
                      </LTIShowHideButton>
                    )}
                  </LTIRadioWrapper>
                  {showKeys && summary.enrollmentType === enrollmentTypes.LTI && (
                    <LTIKeyContainer>
                      <LTIKeySectionContainer>
                        <LTIKeySectionLabel>{intl.formatMessage(messages.sharedKey)}</LTIKeySectionLabel>
                        <CopyInput
                          value={
                            settings.ltiConsumerCredentials
                              ? settings.ltiConsumerCredentials[0].key
                              : intl.formatMessage(messages.missingKey)
                          }
                          labelHidden
                          label={intl.formatMessage(messages.sharedKey)}
                          buttonLabel={intl.formatMessage(messages.copy)}
                          notificationLabel={intl.formatMessage(messages.copyConfirm)}
                        />
                      </LTIKeySectionContainer>
                      <LTIKeySectionContainer>
                        <LTIKeySectionLabel>{intl.formatMessage(messages.sharedSecret)}</LTIKeySectionLabel>
                        <CopyInput
                          value={
                            settings.ltiConsumerCredentials
                              ? settings.ltiConsumerCredentials[0].secret
                              : intl.formatMessage(messages.missingSecret)
                          }
                          labelHidden
                          label={intl.formatMessage(messages.sharedSecret)}
                          buttonLabel={intl.formatMessage(messages.copy)}
                          notificationLabel={intl.formatMessage(messages.copyConfirm)}
                        />
                      </LTIKeySectionContainer>
                    </LTIKeyContainer>
                  )}
                </>
              )}
            </RadioButtonGroup>
          </CardStack>

          <CardStack
            title={intl.formatMessage(messages.appearanceTitle)}
            description={intl.formatMessage(messages.appearanceDescription)}
          >
            <InputWrapper>
              <SimpleColorInput
                label={intl.formatMessage(messages.colorLabel)}
                defaultValue={settings.color || ''}
                onChange={(value) => { updateCohort({ color: value }); }}
              />
            </InputWrapper>

            <InputWrapper>
              <Input
                type="text"
                label={intl.formatMessage(messages.bannerImageLabel)}
                placeholder={intl.formatMessage(messages.bannerImagePlaceholder)}
                defaultValue={settings.bannerImage || ''}
                onBlur={(event) => { updateCohort({ bannerImage: event.target.value }); }}
              />
            </InputWrapper>

            <InputWrapper>
              <Input
                type="text"
                label={intl.formatMessage(messages.bannerPatternLabel)}
                placeholder={intl.formatMessage(messages.bannerPatternPlaceholder)}
                defaultValue={settings.bannerPattern || ''}
                onBlur={(event) => { updateCohort({ bannerPattern: event.target.value }); }}
              />
            </InputWrapper>
          </CardStack>
        </PageContent>
      </PageViewport>
    </PageWrapper>
  );
}

CohortSettings.propTypes = {
  cohort: cohortShape,
  updateCohort: PropTypes.func,
};

const withCohort = enhanceWithCohort(({ match }) => match.params.cohortId);
const flagNames = [featureKeys.WORKSPACE_CLASS_LTI_ENROLLMENT];

const mapDispatchToProps = (dispatch) => ({
  revokePermission: (account, cohortId) => dispatch(commandRevokePermissionOnCohort(account.id, account.type, cohortId)),
});

export default connect(null, mapDispatchToProps)(compose(
  withCohort,
  enhanceWithFeatureFlagSubscription,
)(CohortSettings, flagNames));